import { memo } from "react";
import { AlertTriangle } from "lucide-react";
import type { StockStatus } from "@/features/stocks/types/stock.types";
import {
  STOCK_STATUS_LABEL,
  getUnavailableDays,
} from "../../utils/stock-availability.utils";

interface StockAvailabilityAlertProps {
  statusByDate: Record<string, StockStatus>;
  compact?: boolean;
}

/**
 * Alerta visual de indisponibilidade.
 *
 * Variante `compact` (uma linha com ícone + contagem) para uso ao
 * lado do barcode na lista; variante completa (lista de dias com
 * status) para o card detalhado e o Dialog de confirmação. Não
 * renderiza nada quando a peça está totalmente disponível.
 */
export const StockAvailabilityAlert = memo(function StockAvailabilityAlert({
  statusByDate,
  compact = false,
}: StockAvailabilityAlertProps) {
  const unavailable = getUnavailableDays(statusByDate);
  if (unavailable.length === 0) return null;

  if (compact) {
    return (
      <div className="flex items-center gap-1.5 text-xs text-destructive">
        <AlertTriangle className="w-3.5 h-3.5" aria-hidden="true" />
        <span>Indisponível em {unavailable.length} dia(s)</span>
      </div>
    );
  }

  return (
    <div
      role="alert"
      className="rounded-md border border-destructive/50 bg-destructive/10 p-3 space-y-2"
    >
      <div className="flex items-center gap-2 text-destructive font-medium">
        <AlertTriangle className="w-4 h-4" aria-hidden="true" />
        <span className="text-sm">Peça indisponível em parte do período</span>
      </div>
      <ul className="text-xs text-destructive space-y-0.5 pl-6 list-disc">
        {unavailable.map((day) => (
          <li key={day.date}>
            {day.date} — {STOCK_STATUS_LABEL[day.status]}
          </li>
        ))}
      </ul>
    </div>
  );
});
